import {useContext} from 'react'

import PropTypes from 'prop-types'

import AtomBadge, {atomBadgeDesigns, atomBadgeSizes, atomBadgeTypes} from '@s-ui/react-atom-badge'
import {atomIconSizes} from '@s-ui/react-atom-icon'
import AtomImage from '@s-ui/react-atom-image'
import Context from '@s-ui/react-context'
import MoleculeNotification, {
  moleculeNotificationAlignItems,
  moleculeNotificationBorderSizes,
  moleculeNotificationTypes,
  moleculeNotificationVariations
} from '@s-ui/react-molecule-notification'
import LeadPhoneCall, {
  leadPhoneCallDesignIcon,
  leadPhoneCallTarget,
  leadPhoneCallType
} from '@adv-mt/mt-lead-phone-call'
import BadgeDealerRating from '@adv-mt/mt-rating-badge'
import IconCircleCheck from '@adv-mt/mt-svg-icons/lib/CircleCheck.js'
import IconHeart from '@adv-mt/mt-svg-icons/lib/HeartSolid'
import IconPoi from '@adv-mt/mt-svg-icons/lib/PoiOutline.js'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'

const getAmount = (amount, i18n) => {
  return isNaN(amount) ? '' : i18n.c(amount)
}

const IconFav = () => (
  <div className="mt-PanelVehicleReservation-modalCardVehicleFavNotificationIcon">
    <IconHeart color="currentColor" size={atomIconSizes.medium} />
  </div>
)

export const DEFAULT_IMAGE_URL = 'https://s.ccdn.es/images/illustrations/site-no-image.svg'

export const ModalCardVehicle = ({
  ad,
  financingData,
  dealer,
  isFavorite = false,
  isReserved = false,
  showContact = false
}) => {
  const {i18n, domain} = useContext(Context)
  const config = domain.get('config')
  const {SELLER_TYPES_TRANSLATIONS} = config.get('tracking')
  const {SELLER_TYPE_PROFESSIONAL} = config.get('dealer')
  const sellerType = SELLER_TYPES_TRANSLATIONS[SELLER_TYPE_PROFESSIONAL][config.get('DEFAULT_CULTURE')]

  const {hasCallTrackingService, phoneNumber} = dealer

  const showDealerRating = dealer?.ratings && dealer.ratings?.average >= 4

  return (
    <div className="mt-PanelVehicleReservation-modalCardVehicle">
      <div className="mt-PanelVehicleReservation-modalCardVehicleAd">
        <div className="mt-PanelVehicleReservation-modalCardVehicleAdContent">
          <div className="mt-PanelVehicleReservation-modalCardVehicleAdImageWrapper">
            <AtomImage
              alt={i18n.t('COMMON.IMAGE_ALT_INFO', {alt: i18n.t('COMMON.AD')})}
              title={i18n.t('COMMON.IMAGE_ALT_INFO', {alt: i18n.t('COMMON.AD')})}
              src={ad?.photos?.[0] || DEFAULT_IMAGE_URL}
              className="mt-PanelVehicleReservation-modalCardVehicleAdImage"
            />
          </div>
          <div className="mt-PanelVehicleReservation-modalCardVehicleAdInfo">
            {isReserved ? (
              <AtomBadge
                design={atomBadgeDesigns.SOFT}
                icon={<IconCircleCheck color="currentColor" size={atomIconSizes.small} />}
                label={i18n.t('COMMON.RESERVED')}
                size={atomBadgeSizes.LARGE}
                type={atomBadgeTypes.SUCCESS}
              />
            ) : (
              <AtomBadge
                design={atomBadgeDesigns.SOFT}
                size={atomBadgeSizes.LARGE}
                type={atomBadgeTypes.INFO}
                label={i18n.t('COMMON.RESERVATION_AVAILABLE')}
              />
            )}
            <TitleBasic as={titleBasicTags.h3} maxTitleLines={1} size={titleBasicSizes.xs} title={ad?.title} />
            {`${getAmount(ad?.price, i18n)} ${financingData?.fee ? '-' : ''} ${getAmount(financingData?.fee, i18n)}${
              financingData?.fee ? '/mes' : ''
            }`}
          </div>
        </div>
        {isFavorite && (
          <div className="mt-PanelVehicleReservation-modalCardVehicleFavNotification">
            <MoleculeNotification
              alignItems={moleculeNotificationAlignItems.CENTER}
              autoClose={null}
              icon={<IconFav />}
              position="relative"
              roundedCorners={moleculeNotificationBorderSizes.medium}
              show
              showCloseButton={false}
              showLeftIconMobile
              type={moleculeNotificationTypes.system}
              variation={moleculeNotificationVariations.positive}
            >
              {i18n.t('VEHICLE_RESERVATION.MODAL_PAYMENT_SUCCESS.NOTIFICATION_FAV')}
            </MoleculeNotification>
          </div>
        )}
      </div>
      <hr className="mt-PanelVehicleReservation-modalCardVehicleBreak" />
      <div className="mt-PanelVehicleReservation-modalCardVehicleDealer">
        <div className="mt-PanelVehicleReservation-modalCardVehicleDealerName">
          <TitleBasic as={titleBasicTags.h3} size={titleBasicSizes.xs} title={dealer?.name} />
          {showDealerRating && <BadgeDealerRating value={dealer?.ratings?.average} />}
        </div>
        {showContact ? (
          <div className="mt-PanelVehicleReservation-modalCardVehicleDealerContact">
            <div className="mt-PanelVehicleReservation-modalCardVehicleDealerContactAddress">
              <IconPoi color="currentColor" size={atomIconSizes.medium} />
              <div className="mt-PanelVehicleReservation-modalCardVehicleDealerContactAddressText">
                {dealer?.location?.address}
                <br />
                {dealer?.location?.postalCode}
                {` `}
                {dealer?.location?.city}
              </div>
            </div>
            <div className="mt-PanelVehicleReservation-modalCardVehicleDealerContactPhone">
              <LeadPhoneCall
                adId={ad.id}
                designIcon={leadPhoneCallDesignIcon.OUTLINE}
                extendedLeadTrackingData={{seller_type: sellerType}}
                hasCallTrackingService={hasCallTrackingService}
                phone={phoneNumber}
                target={leadPhoneCallTarget.reservationPaymentConfirmed}
                type={leadPhoneCallType.link}
              />
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  )
}

ModalCardVehicle.displayName = 'ModalCardVehicle'
ModalCardVehicle.propTypes = {
  ad: PropTypes.object,
  financingData: PropTypes.object,
  dealer: PropTypes.object,
  isFavorite: PropTypes.bool,
  isReserved: PropTypes.bool,
  showContact: PropTypes.bool
}
